import type { TravelSettings } from './types';
import { FSA_COORDS } from './fsaData';

/** Uppercase and strip everything but letters/digits: "k2p 1l4" → "K2P1L4". */
export const normalizePostal = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '');

/** First 3 characters of a Canadian postal code (the FSA), e.g. "K2P". */
const FSA_PATTERN = /^[A-Z]\d[A-Z]/;

/** Looks up a postal code's FSA. Returns null if it isn't a valid/known Canadian code. */
export function fsaInfo(raw: string): { fsa: string; lat: number; lng: number; area: string } | null {
  const code = normalizePostal(raw);
  if (!FSA_PATTERN.test(code)) return null;
  const fsa = code.slice(0, 3);
  const entry = FSA_COORDS[fsa];
  if (!entry) return null;
  return { fsa, lat: entry[0], lng: entry[1], area: entry[2] };
}

/** Great-circle distance in km. */
export function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export type PostalCheck =
  | { status: 'unavailable' } // mobile service turned off
  | { status: 'invalid' } // not a valid Canadian postal code
  | { status: 'ok'; fee: number; zoneName: string; area: string; distanceKm: number }
  | { status: 'outside'; area: string };

/**
 * Checks a Canadian postal code against the configured service regions.
 * A region is a center postal code + radius (km): the customer's FSA
 * centroid must fall inside the circle. When several regions match, the
 * nearest center wins. Everything outside every region is blocked.
 */
export function checkPostal(raw: string, travel: TravelSettings): PostalCheck {
  if (travel.mode === 'off') return { status: 'unavailable' };
  const info = fsaInfo(raw);
  if (!info) return { status: 'invalid' };
  if (travel.mode === 'flat') {
    return { status: 'ok', fee: travel.flatFee, zoneName: 'our service area', area: info.area, distanceKm: 0 };
  }

  let best: { fee: number; zoneName: string; distanceKm: number } | null = null;
  for (const region of travel.regions) {
    const center = fsaInfo(region.centerPostal);
    if (!center) continue; // misconfigured region — skip rather than block everyone
    const d = distanceKm(info.lat, info.lng, center.lat, center.lng);
    if (d <= region.radiusKm && (!best || d < best.distanceKm)) {
      best = { fee: region.fee, zoneName: region.name || center.area, distanceKm: d };
    }
  }
  return best
    ? { status: 'ok', ...best, area: info.area }
    : { status: 'outside', area: info.area };
}
